/**
 * Filterable, infinitely-scrollable table of companies with inline edit support.
 *
 * @module components/admin/CompanyTable
 * @author Snowfly
 * @version 1.0.0
 */
import React, {useState, useEffect} from 'react';
import PropTypes from "prop-types";
import {connect} from "react-redux";
import TableContainer from "@material-ui/core/TableContainer";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import TableSortLabel from "@material-ui/core/TableSortLabel";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import AdminsPermissions from "../../permissions/admin";
import TableBody from "@material-ui/core/TableBody";
import Avatar from "@material-ui/core/Avatar";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import InfiniteScroll from "react-infinite-scroll-component";
import {
    makeStyles,
} from '@material-ui/core/styles';
import CompanyTableStyle from "./CompanyTable.js";
import permissionCheck from "../../utils/permissionCheck";
import moment from "moment";
import splashPlaceholder from '../../assets/images/login/image_placeholder.png';
import logoPlaceholder from '../../assets/images/login/logo_placeholder.gif';
import EditCompanyModal from "./EditCompanyModal";
import {fetchCompanies} from '../../redux/admin/action.js';
import {addCompany, saveCompany} from '../../services/result.service.js';
import {Button, FormControlLabel, Grid} from "@material-ui/core";
import useAlert from "../common/UseAlert";
import Switch from "@material-ui/core/Switch";

/**
 * Represents a functional React component for managing the companies' table.
 *
 * This component provides a user interface for displaying, filtering, and managing
 * a list of companies. It supports pagination, editing, toggling active/inactive
 * companies, and managing company-related information through an editor modal.
 *
 * State Management:
 * - Manages the active/inactive status of companies.
 * - Handles page number for pagination.
 * - Controls visibility of the edit modal and tracks the company being edited.
 *
 * Features:
 * - Fetches the list of companies if not available.
 * - Allows toggling between showing active/inactive companies.
 * - Triggers pagination when the user scrolls for more companies.
 * - Displays company data in a sortable table.
 *
 * Props:
 * @property {Function} fetchCompanies - A function to fetch the list of companies.
 * @property {Array} companyList - An array of all company objects.
 * @property {Object|null} editCompany - A specific company object to edit or null.
 * @property {Function} showEditor - A function to toggle the visibility of the editor interface.
 */
const CompanyTable = ({
    fetchCompanies,
    companyList,
    showEditor,
    addingCompany,
    searchText,
}) => {
    const user = JSON.parse(localStorage.getItem('user'));
    const defaultCompany = {
        name: '',
        domain: '',
        active: true,
        zonkerUrl: '',
        zonkerKey: '',
        zonkerSecret: '',
        logoUrl: '',
        splashUrl: '',
        creator: user
    };
    const useStyles = makeStyles((theme) => CompanyTableStyle(theme));
    const classes = useStyles();
    const {setAlert} = useAlert();

    const [pageNumber, setPageNumber] = useState(0);
    const [showEditModal, setShowEditModal] = useState(false);
    const [companyToEdit, setCompanyToEdit] = useState(defaultCompany);
    const [onlyActiveCompanies, setOnlyActiveCompanies] = useState(true);
    const [sortBy, setSortBy] = useState('name');
    const [order, setOrder] = useState('asc');

    useEffect(
        () => {
            fetchCompanies(sortBy, order, searchText, onlyActiveCompanies, pageNumber*10);
        },
        [sortBy, order, searchText, onlyActiveCompanies, pageNumber]
    );

    useEffect(
        () => {
            if (addingCompany) {
                setCompanyToEdit(defaultCompany);
            }
            setShowEditModal(addingCompany);
        },
        [addingCompany]
    );

    /**
     * Updates the current page number by incrementing it by one.
     *
     * This function modifies the state of the page number by accessing its
     * previous value and incrementing it. It is intended to be used for handling
     * pagination and facilitating navigation between pages.
     */
    const handlePageChange = () => {
        setPageNumber((prevPageNumber) => prevPageNumber + 1);
    };

    /**
     * Handles the event triggered when an image is loaded.
     *
     * This function is an event handler designed to manage the visibility
     * of the loaded image and its subsequent sibling element. When the
     * image is loaded, it displays the image and hides the next sibling
     * element.
     *
     * @param {Event} evt - The event object containing details about the image load event.
     *                      The `target` property should refer to the image element.
     */
    const handleImageLoaded = (evt) => {
        let img = evt.target,
            next = img.nextElementSibling;

        img.style.display = 'inherit';
        next.style.display = 'none';
    };

    /**
     * Handles the closing of a modal or editor component.
     * This function performs the following actions:
     * - Logs the event object for debugging purposes.
     * - Hides the edit modal by setting the show state to false.
     * - Resets the company being edited to its default state.
     * - Hides the editor interface.
     *
     * @param {Object} evt - The event object triggered by the close action.
     */
    const handleClose = (evt) => {
        console.log(evt);
        setShowEditModal(false);
        setCompanyToEdit(defaultCompany);
        showEditor(false);
    };

    /**
     * Handles the logic for closing the dialog and saving or adding a company.
     *
     * This asynchronous function checks if the company being edited has an existing ID.
     * If an ID exists, it calls the `saveCompany` function to update the company.
     * Otherwise, it calls the `addCompany` function to create a new company.
     *
     * On successful completion, the function:
     * - Closes the edit modal by setting the modal visibility state.
     * - Resets the `companyToEdit` data to a default company structure.
     * - Toggles the editor off.
     * - Refreshes the list of companies by invoking the `fetchCompanies` function.
     *
     * @param {Object} evt - The event object passed to the function when triggered.
     * @returns {Promise<void>} - A Promise that resolves when the dialog close and processing is
     *      completed.
     */
    const handleCloseDialog = async (/* evt */) => {
        let result = companyToEdit._id ?
            await saveCompany(companyToEdit) :
            await addCompany(companyToEdit);

        if (result?.success) {
            setShowEditModal(false);
            setCompanyToEdit(defaultCompany);
            showEditor(false);
            fetchCompanies();
            setAlert(
                'The Company was updated successfully',
                'success'
            );
            return true;
        }

        setAlert(
            'Could not update Company: '+result.message,
            'error'
        );
        return false;
    };

    /**
     * Handles the edit action for a company.
     *
     * This function sets the company to be edited in the state and displays the edit modal.
     *
     * @param {Object} company - The company object to be edited.
     */
    const handleEditCompany = (company) => {
        setCompanyToEdit(company);
        setShowEditModal(true);
    };

    /**
     * Toggles the state of the `onlyActiveCompanies` variable.
     *
     * This function switches the current state of `onlyActiveCompanies`
     * between `true` and `false`. It is typically used to control
     * whether only active companies are displayed in a given context.
     */
    const handleToggleActive = () => {
        setOnlyActiveCompanies(!onlyActiveCompanies);
    }

    /**
     * A function to handle sorting logic for a data set by toggling the order and setting the property to sort by.
     *
     * @param {Object} event - The event object triggered by the user action, often used to access metadata or prevent default behavior.
     * @param {string} property - The name of the property to sort by.
     */
    const handleSort = (event, property) => {
        setOrder(
            (sortBy === property && order === 'desc') ? 'asc' : 'desc'
        );
        setSortBy(property);
    }

    return (
        <>
            <Grid container>
                <Grid item xs={12}>
                    <FormControlLabel label="Active Companies Only"
                                      control={<Switch checked={onlyActiveCompanies}
                                                       value={1}
                                                       id="onlyActiveCompanies"
                                                       onClick={handleToggleActive}
                                      />}
                    />
                </Grid>
                <Grid item xs={12}>
                    <InfiniteScroll
                        dataLength={companyList.length}
                        next={handlePageChange}
                        hasMore={companyList.length < 1}
                        loader={<p>Loading...</p>}
                    >
                        <TableContainer component={Paper}>
                            <Table aria-label="Company Manage Table" className={classes.root}>
                                <TableHead className={classes.thead}>
                                    <TableRow className={classes.tableRow}>
                                        <TableCell className={classes.heading}>
                                            <TableSortLabel
                                                direction={sortBy === 'name' ? order : "desc"}
                                                active
                                                IconComponent={ArrowDropDownIcon}
                                                onClick={(event) => handleSort(event, 'name')}
                                            >
                                                NAME
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell className={classes.heading}>
                                            <TableSortLabel
                                                direction={sortBy === 'domain' ? order : "desc"}
                                                active
                                                IconComponent={ArrowDropDownIcon}
                                                onClick={(event) => handleSort(event, 'domain')}
                                            >
                                                DOMAIN
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell className={classes.heading}>
                                            <TableSortLabel
                                                direction={sortBy === 'active' ? order : "desc"}
                                                active
                                                IconComponent={ArrowDropDownIcon}
                                                onClick={(event) => handleSort(event, 'active')}
                                            >
                                                ACTIVE
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell className={classes.heading}>
                                            <TableSortLabel
                                                direction={sortBy === 'createdBy' ? order : "desc"}
                                                active
                                                IconComponent={ArrowDropDownIcon}
                                                onClick={(event) => handleSort(event, 'createdBy')}
                                            >
                                                CREATED BY
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell className={classes.heading}>
                                            <TableSortLabel
                                                direction={sortBy === 'createdOn' ? order : "desc"}
                                                active
                                                IconComponent={ArrowDropDownIcon}
                                                onClick={(event) => handleSort(event, 'createdOn')}
                                            >
                                                CREATED ON
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell className={classes.heading}>
                                            SEND TO SNOWFLY URL
                                        </TableCell>
                                        <TableCell className={classes.heading}>
                                            LOGO
                                        </TableCell>
                                        <TableCell className={classes.heading}>
                                            SPLASH IMAGE
                                        </TableCell>
                                        {
                                            user.super_admin || permissionCheck(
                                                AdminsPermissions.companies.update.task,
                                                AdminsPermissions.companies.update.type,
                                            )
                                                ? (
                                                    <TableCell className={classes.heading}>
                                                        EDIT
                                                    </TableCell>
                                                )
                                                : null
                                        }

                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {companyList?.length > 0 && companyList.map((row) => (
                                        <TableRow
                                            key={row._id}
                                            className={classes.tableRow}
                                        >
                                            <TableCell className={classes.bold}>
                                                {row.name}
                                            </TableCell>
                                            <TableCell className={classes.lastNameCell}>
                                                {row.domain}
                                            </TableCell>
                                            <TableCell className={classes.lastNameCell}>
                                                {row.active ? 'YES': 'NO'}
                                            </TableCell>
                                            <TableCell className={classes.lastNameCell}>
                                                <div className={classes.built}>
                                                    {row.creator ? (
                                                            <>
                                                                <Avatar
                                                                    src={row.creator?.profile_pic ?? ''}
                                                                    alt="profile"
                                                                    style={{marginRight: '1em'}}
                                                                    className={classes.small}
                                                                />
                                                                <div className={classes.firstNameCell}>
                                                                    {row.creator?.name ?? 'System'}
                                                                </div>
                                                            </>
                                                        ) :
                                                        (
                                                            <span>N/A</span>
                                                        )
                                                    }
                                                </div>
                                            </TableCell>
                                            <TableCell align="center" className={classes.no_of_members}>
                                                {moment(row.createdAt).format('MM-DD-YYYY')}
                                            </TableCell>
                                            <TableCell>
                                                {row.zonkerUrl}
                                            </TableCell>
                                            <TableCell align="left" className={classes.no_of_members}>
                                                <img src={row.logoUrl}
                                                     alt=""
                                                     width="48"
                                                     style={{width: '48px', height: '48px', display: 'none'}}
                                                     onLoad={handleImageLoaded}
                                                />
                                                <img src={logoPlaceholder}
                                                     width="48"
                                                     alt=""
                                                     style={{width: '48px', height: '48px'}}
                                                />
                                            </TableCell>
                                            <TableCell align="left" className={classes.no_of_members}>
                                                <img src={row.splashUrl}
                                                     alt=""
                                                     width="128"
                                                     style={{width: '128px', height: 'auto', display: 'none'}}
                                                     onLoad={handleImageLoaded}
                                                />
                                                <img src={splashPlaceholder}
                                                     alt=""
                                                     width="128"
                                                     style={{width: '128px', height: 'auto'}}
                                                />
                                            </TableCell>
                                            {
                                                user.super_admin || permissionCheck(
                                                    AdminsPermissions.companies.update.task,
                                                    AdminsPermissions.companies.update.type,
                                                ) ? (
                                                        <TableCell>
                                                            <Button variant="contained"
                                                                    size="small"
                                                                    className={classes.editIcon}
                                                                    disableElevation
                                                                    onClick={() => handleEditCompany(row)}
                                                            >
                                                                <EditOutlinedIcon className={classes.editIcon} />
                                                            </Button>
                                                        </TableCell>
                                                    )
                                                    : null
                                            }

                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </InfiniteScroll>
                </Grid>
            </Grid>

            <EditCompanyModal visible={showEditModal}
                              onClose={handleClose}
                              onSave={handleCloseDialog}
                              classes={classes}
                              company={companyToEdit}
                              setCompany={setCompanyToEdit}
            />
        </>
    );
};

/**
 * PropTypes definition for the CompanyTable component.
 *
 * This specifies the expected types for the props passed to the CompanyTable
 * component to ensure the correct usage and reduce potential runtime errors.
 *
 * The component renders a table displaying information specific to companies.
 * Each prop must follow the defined type and structure to ensure proper behavior.
 *
 * Props:
 * - companies: An array of objects, where each object represents a company and
 *   contains the details to be displayed in the table.
 * - onRowClick: A function to be invoked when a table row is clicked. It receives
 *   the company object corresponding to the row as an argument.
 * - loading: A boolean indicating whether the table is in a loading state. When
 *   true, the content may be replaced with a loading indicator.
 * - error: A string or null, representing any error message to be displayed if
 *   data fetching fails or some other issue occurs.
 * - columns: An array of strings specifying the column headers for the table.
 *   Each string represents a column name to display.
 */
CompanyTable.propTypes = {
    companyList: PropTypes.array,
    fetchCompanies: PropTypes.func,
    showEditor: PropTypes.func,
    addingCompany: PropTypes.bool,
    searchText: PropTypes.string,
};

/**
 * A function used to map the current state from the Redux store to the props of a component.
 *
 * @param {Object} state - The current state of the Redux store.
 * @returns {Object} An object that contains the `companyList` property, which corresponds to the
 *     list of companies under the `admin.companies` state in the Redux store.
 */
const mapStateToProps = (state) => ({
    companyList: state.admin.companies,
});

/**
 * Maps dispatchable action creators to component props, enabling the component
 * to dispatch actions to the Redux store.
 *
 * @constant {Object} mapDispatchToProps
 * @property {Function} fetchCompanies - Action creator responsible for initiating
 * the process of fetching companies from the backend or API and dispatching
 * the corresponding actions to update the Redux store.
 */
const mapDispatchToProps = {
    fetchCompanies
};

export default connect(mapStateToProps, mapDispatchToProps)(CompanyTable);
